import {
  ActionIcon,
  Box,
  Button,
  Divider,
  FileButton,
  Flex,
  Grid,
  Group,
  Image,
  Input,
  Select,
  Switch,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import {
  IconEdit,
  IconPlus,
  IconSearch,
  IconTrash,
  IconFileUpload,
  IconFileDownload,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useEffect, useMemo, useState } from "react";
import BreadCrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { _localization } from "../../../config/location";
import CreateView from "./CreateView";
import DeleteView from "./DeleteView";
import EditView from "./EditView";
import { TblWarranteeBrandCategory } from "../../../model/TblWarranteeBrandCategory";
import { useLocation } from "react-router-dom";

import { getListBrandCategory, modifyBrandCategory, importBrandCategory } from "../../../api/ApiBrandCategory";
import { getDataEmployeekdol } from "../../../api/ApiEmployee";
import moment from "moment";
import * as xlsx from "xlsx";

const WarranteeBrandCategory = () => {
  const location = useLocation();
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [tempSearchKeyword, setTempSearchKeyword] = useState("");
  const [tempStatusFilter, setTempStatusFilter] = useState<string | null>(null);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [data, setData] = useState<TblWarranteeBrandCategory[]>([]);
  const [total, setTotal] = useState(0);

  const fetchBrandCategory = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      let query = `Skip=${pagination.pageIndex * pagination.pageSize}&Take=${pagination.pageSize}`;
      if (searchKeyword.trim()) {
        query += `&KeySearch=${encodeURIComponent(searchKeyword.trim())}`;
      }
      if (statusFilter !== null && statusFilter !== "") {
        query += `&Status=${statusFilter}`;
      }
      const response = await getListBrandCategory(query);
      if (Array.isArray(response)) {
        setData(response);
        setTotal(response.length);
      } else if (response && response.success && response.data) {
        if (Array.isArray(response.data)) {
          setData(response.data);
          setTotal(response.totalCount || response.data.length);
        } else if (response.data.lists) {
          setData(response.data.lists || []);
          setTotal(response.data.count || 0);
        } else {
          setData([]);
          setTotal(0);
        }
      } else if (response && Array.isArray(response.lists)) {
        setData(response.lists);
        setTotal(response.count || response.lists.length);
      } else if (response && Array.isArray(response.data)) {
        setData(response.data);
        setTotal(response.data.length);
      } else {
        setData([]);
        setTotal(0);
      }
    } catch (e) {
      setIsError(true);
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBrandCategory();
  }, [pagination.pageIndex, pagination.pageSize, searchKeyword, statusFilter]);

  const handleSearch = () => {
    setSearchKeyword(tempSearchKeyword);
    setStatusFilter(tempStatusFilter);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleReset = () => {
    setTempSearchKeyword("");
    setTempStatusFilter(null);
    setSearchKeyword("");
    setStatusFilter(null);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = React.useMemo<MRT_ColumnDef<TblWarranteeBrandCategory>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        size: 80,
      },
      {
        accessorKey: "logoImg",
        header: "Logo",
        size: 100,
        Cell: ({ cell }) => (
          <Box style={{ width: 80, height: 40, display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #eee", borderRadius: 4, padding: 4 }}>
            <Image
              src={cell.getValue<string>() || ""}
              alt="Brand Logo"
              fit="contain"
              fallbackSrc="https://via.placeholder.com/80x40?text=No+Logo"
            />
          </Box>
        ),
      },
      {
        accessorKey: "name",
        header: "Tên hãng",
        size: 200,
      },
      {
        accessorKey: "orderNumber",
        header: "Số Thứ tự",
        size: 100,
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        size: 150,
        Cell: ({ row }) => (
          <Flex align="center" gap="xs">
            <Switch
              checked={row.original.status === "A"}
              color="blue"
              size="xs"
              onChange={async () => {
                const newStatus = row.original.status === "A" ? "I" : "A";
                const updatedItem = {
                  ...row.original,
                  status: newStatus,
                };
                const res = await modifyBrandCategory(updatedItem);
                if (res && res.success) {
                  fetchBrandCategory();
                }
              }}
            />
            <Text size="sm">
              {row.original.status === "A" ? "Đang hoạt động" : "Không hoạt động"}
            </Text>
          </Flex>
        ),
      },
      {
        accessorKey: "createDate",
        header: "Ngày đăng tải",
        size: 180,
        Cell: ({ cell }) => cell.getValue<string>() ? moment(cell.getValue<string>()).format("DD-MM-YYYY HH:mm:ss") : "",
      },
      // {
      //   accessorKey: "createBy",
      //   header: "Người viết",
      //   size: 150,
      //   Cell: ({ cell }) => {
      //     const val = cell.getValue<string>();
      //     if (!val) return "";
      //     const emp = employees.find((e) => e.id === parseInt(val) || e.code === val);
      //     return emp ? emp.name : val;
      //   },
      // },
      {
        accessorKey: "url",
        header: "Đường dẫn",
        size: 180,
        Cell: ({ cell }) => (
          <Text
            component="a"
            href={`${process.env.REACT_APP_BASE_WEB_HACOM_URL}/bao-hanh/${cell.getValue<string>()}` || "#"}
            target="_blank"
            c="blue"
            size="sm"
            style={{ textDecoration: "underline", cursor: "pointer" }}
          >
            Xem trang bài viết
          </Text>
        ),
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => (
          <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Tooltip label="Chỉnh sửa">
              <ActionIcon
                variant="light"
                color="yellow"
                onClick={() => editItem(row.original.id)}
              >
                <IconEdit size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Xóa">
              <ActionIcon
                variant="light"
                color="red"
                onClick={() => deleteItem([row.original.id])}
              >
                <IconTrash size={20} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [data]
  );

  const openModal = () =>
    modals.openConfirmModal({
      title: <Title order={5}>Thêm mới hãng bảo hành</Title>,
      children: <CreateView onSearch={fetchBrandCategory} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
      size: "xl",
    });

  const deleteItem = (idItem: number[]) => {
    modals.openConfirmModal({
      title: <Title order={5}>Xóa hãng bảo hành</Title>,
      children: <DeleteView onSearch={fetchBrandCategory} idItem={idItem} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const editItem = (id: number) => {
    modals.openConfirmModal({
      title: <Title order={5}>Chỉnh sửa hãng bảo hành</Title>,
      children: <EditView id={id} onSearch={fetchBrandCategory} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
      size: "xl",
    });
  };

  const handleImportExcel = async (file: File) => {
    if (!file) return;

    const fileType = file.name.split(".").pop()?.toLowerCase();
    if (fileType !== "xlsx" && fileType !== "xls") {
      NotificationExtension.Warn("Vui lòng chọn file Excel (.xlsx, .xls) !");
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("File", file);
      formData.append("FormFile", file);

      const response = await importBrandCategory(formData);
      if (response && (response.success || (response.data && response.data.failedRows === 0))) {
        fetchBrandCategory();
      }
    } catch (error) {
      console.error("Import error:", error);
      NotificationExtension.Fails("Có lỗi xảy ra khi import !");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      const data = [
        ["ĐỊA ĐIỂM BẢO HÀNH HÃNG", "", "", "", "", "", "", "", "", ""],
        ["STT", "HÃNG", "LOGO", "KHU VỰC", "ĐỊA CHỈ", "SẢN PHẨM NHẬN BẢO HÀNH", "Tổng đài", "ĐIỆN THOẠI/ FAX", "LINK WEB Check BH", "Link CSBH"],
        [1, "Dell", "https://hanoicomputercdn.com/media/brand/dell.png", "Thành phố Hồ Chí Minh", "23 Nguyễn Thị Huỳnh, Phường Phú Nhuận, Thành phố Hồ Chí Minh", "Laptop, Màn hình", "1800 4851 - 028.38423333 - 028.38424342", "", "https://www.dell.com/support/contractservices/en-vn/", "https://www.dell.com/en-vn/lp/legal/terms-of-sale-consumer-service-contracts-support-featured-agreements"],
        [1, "Dell", "https://hanoicomputercdn.com/media/brand/dell.png", "Thành phố Hà Nội", "26 Yên Lãng, Phường Láng, Thành phố Hà Nội", "Laptop, Màn hình", "1800 4851 - 024.35375858 - 024 38515666 - 1900 252558", "", "https://www.dell.com/support/contractservices/en-vn/", "https://www.dell.com/en-vn/lp/legal/terms-of-sale-consumer-service-contracts-support-featured-agreements"]
      ];

      const worksheet = xlsx.utils.aoa_to_sheet(data);

      // Thiết lập merge ô tiêu đề dòng đầu tiên (Cột A đến J)
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }
      ];

      // Thiết lập độ rộng các cột
      worksheet["!cols"] = [
        { wch: 6 },   // STT
        { wch: 10 },  // HÃNG
        { wch: 30 },  // LOGO
        { wch: 25 },  // KHU VỰC
        { wch: 50 },  // ĐỊA CHỈ
        { wch: 25 },  // SẢN PHẨM NHẬN BẢO HÀNH
        { wch: 45 },  // Tổng đài
        { wch: 15 },  // ĐIỆN THOẠI/ FAX
        { wch: 40 },  // LINK WEB Check BH
        { wch: 40 }   // Link CSBH
      ];

      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Sheet1");
      xlsx.writeFile(workbook, "Mau_Import_Dia_Diem_Bao_Hanh_Hang.xlsx");
      NotificationExtension.Success("Tải file mẫu thành công!");
    } catch (error) {
      console.error("Error downloading template:", error);
      NotificationExtension.Fails("Tải file mẫu thất bại!");
    }
  };

  const table = useMantineReactTable({
    columns,
    data,
    rowCount: total,
    manualPagination: true,
    getRowId: (row) => row.id?.toString(),
    enableColumnResizing: true,
    initialState: {
      density: "xs",
      columnPinning: { right: ["action"] },
    },
    state: {
      isLoading,
      pagination,
      rowSelection,
    },
    onPaginationChange: setPagination,
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    localization: _localization,
  });

  useEffect(() => {
    const handleResize = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      setHeight(window.innerHeight - (170 + headerHeight));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <Box>
      <Group gap={0} justify="space-between" mb={10}>
        <BreadCrumb />
      </Group>
      <div style={{ border: "1px solid #dee2e6", padding: "10px 12px", borderRadius: "6px", background: "#f8f9fa", marginBottom: "12px" }} ref={headerRef}>
        <Flex justify="space-between" align="center" gap="md" wrap="wrap">
          <Group gap="xs" style={{ flex: 1 }}>
            <Input
              placeholder="Từ khóa..."
              leftSection={<IconSearch size={14} color="#15aabf" />}
              value={tempSearchKeyword}
              onChange={(e) => setTempSearchKeyword(e.currentTarget.value)}
              styles={{ input: { borderRadius: "6px", height: "32px", fontSize: "13px" } }}
              style={{ width: "160px" }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
            <Select
              placeholder="Trạng thái"
              data={[
                { value: "", label: "Tất cả trạng thái" },
                { value: "A", label: "Hoạt động" },
                { value: "I", label: "Không hoạt động" },
              ]}
              value={tempStatusFilter}
              onChange={tempStatusFilter => setTempStatusFilter(tempStatusFilter)}
              clearable
              styles={{ input: { borderRadius: "6px", height: "32px", fontSize: "13px" } }}
              style={{ width: "160px" }}
            />
            <Button
              leftSection={<IconSearch size={12} />}
              color="teal"
              size="xs"
              onClick={handleSearch}
              style={{ borderRadius: "6px", height: "32px" }}
            >
              Tìm kiếm
            </Button>
            <Button
              color="gray"
              variant="light"
              size="xs"
              onClick={handleReset}
              style={{ borderRadius: "6px", height: "32px" }}
            >
              Đặt lại
            </Button>
          </Group>
          <Group gap="xs">
            <FileButton
              onChange={(file) => {
                if (file) {
                  handleImportExcel(file);
                }
              }}
              accept=".xls,.xlsx"
            >
              {(props) => (
                <Button
                  leftSection={<IconFileUpload size={12} />}
                  color="green"
                  size="xs"
                  {...props}
                  style={{ borderRadius: "6px", height: "32px" }}
                >
                  Import Excel
                </Button>
              )}
            </FileButton>
            <Button
              leftSection={<IconFileDownload size={12} />}
              color="orange"
              size="xs"
              onClick={handleDownloadTemplate}
              style={{ borderRadius: "6px", height: "32px" }}
            >
              Tải file mẫu
            </Button>
            <Button
              leftSection={<IconPlus size={12} />}
              color="blue"
              size="xs"
              onClick={openModal}
              style={{ borderRadius: "6px", height: "32px" }}
            >
              Thêm mới
            </Button>
            <Button
              leftSection={<IconTrash size={12} />}
              color="red"
              variant="light"
              size="xs"
              onClick={() => {
                const selectedIds = Object.keys(rowSelection).map(Number);
                if (selectedIds.length === 0) {
                  NotificationExtension.Warn("Vui lòng chọn dữ liệu !");
                  return;
                }
                deleteItem(selectedIds);
              }}
              disabled={Object.keys(rowSelection).length === 0}
              style={{ borderRadius: "6px", height: "32px" }}
            >
              Xóa (Đã chọn)
            </Button>
          </Group>
        </Flex>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </Box>
  );
};

export default WarranteeBrandCategory;
